import { makeValueFormatter } from "@/components/cinch/charts";
import { cn } from "@/lib/utils";

import { type PanelVizSpec, type QueryResultLike } from "../viz";

/** Pick the value column for a stat panel: the configured `valueKey`, else the
 *  first column whose first-row value is numeric, else the first column. */
function pickValueKey(result: QueryResultLike, viz: PanelVizSpec): string {
  if (viz.valueKey && result.columns.includes(viz.valueKey)) return viz.valueKey;
  const numeric = result.columns.find((c) => {
    const v = result.rows[0]?.[c];
    return v != null && v !== "" && Number.isFinite(Number(v));
  });
  return numeric ?? result.columns[0] ?? "";
}

/**
 * A single big-number KPI tile (panel type "stat"): the first row's value in
 * the chosen column, formatted. The panel title (card / grid header) is the
 * label, so this shows just the number + the source column as a caption.
 */
export function StatView({
  result,
  viz,
  className,
}: {
  result: QueryResultLike;
  viz: PanelVizSpec;
  className?: string;
}) {
  const key = pickValueKey(result, viz);
  const raw = result.rows[0]?.[key];
  const n = raw == null || raw === "" ? null : Number(raw);
  const fmt = makeValueFormatter(viz.valueFormat ?? "count");
  const display = n != null && Number.isFinite(n) ? fmt(n) : raw == null ? "—" : String(raw);
  return (
    <div
      className={cn(
        "flex h-full flex-col items-center justify-center gap-1 p-3 text-center",
        className,
      )}
    >
      <span className="text-4xl font-bold tabular-nums text-foreground">{display}</span>
      {key && <span className="text-xs text-muted-foreground">{key}</span>}
    </div>
  );
}
